// Lịch Sử Học Tập Page (bản đầy đủ)
// NGUỒN SỰ THẬT = backend /study (giống Tab "Lịch sử học tập" trong Hồ sơ) → đồng bộ đa thiết bị.
// Trang này = bản xem TẤT CẢ (bài học, trắc nghiệm, tự luận, thành tích); Hồ sơ chỉ hiện tóm tắt.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { achievementService } from '../services/studyHistory/AchievementService';
import { API_CONFIG } from '../config/api.config';
import { Achievement } from '../types/learningHistory';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import PmakerAttemptDetailModal from '../components/history/PmakerAttemptDetailModal';
import {
  BookOpen,
  Clock,
  Target,
  BarChart3,
  Trophy,
  FileText,
} from 'lucide-react';

type LessonRow = { id: string; lessonId: string; lessonTitle: string; progress: number; timeSpent: number; status: string };
type QuizRow = { id: string; quizTitle: string; attemptNumber: number; passed: boolean; score: number; correctAnswers: number; wrongAnswers: number; _scoreText: string; _date: any; _type?: string; _meta?: string };
type EssayRow = { id: string; title: string; passed: boolean; scoreText: string; _date: any };
type Stats = { completedLessons: number; totalLessons: number; totalQuizzes: number; averageScore: number; totalStudyTime: number };

export default function LichSuHocTap() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'lessons' | 'quizzes' | 'essays' | 'achievements'>('overview');

  const [statistics, setStatistics] = useState<Stats | null>(null);
  const [lessonProgress, setLessonProgress] = useState<LessonRow[]>([]);
  const [quizHistory, setQuizHistory] = useState<QuizRow[]>([]);
  const [essayHistory, setEssayHistory] = useState<EssayRow[]>([]);
  const [essayStats, setEssayStats] = useState<{ count: number; avg: number }>({ count: 0, avg: 0 });
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [attemptDetail, setAttemptDetail] = useState<any>(null);
  const [loadingAttemptId, setLoadingAttemptId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    loadAllData();
  }, [user?.id]);

  const loadAllData = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const base = API_CONFIG.python.apiUrl;
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      const authFetch = async (path: string) => {
        try { const r = await fetch(`${base}${path}`, { headers: { Authorization: `Bearer ${token}` } }); return r.ok ? await r.json() : null; }
        catch { return null; }
      };
      const [stats, progList, quizList, assessRes, achievs] = await Promise.all([
        authFetch('/study/progress/stats'),
        authFetch('/study/progress'),
        authFetch('/study/quiz-results?limit=100'),
        authFetch('/study/my-assessments'),   // đề PMaker + bài tập tự luận đã chấm
        achievementService.getUserAchievements(user.id).catch(() => []),
      ]);
      const quizzes: any[] = Array.isArray(quizList) ? quizList : [];
      const assess: any[] = Array.isArray(assessRes?.items) ? assessRes.items : [];
      const exams = assess.filter((a) => a.type === 'exam' || a.type === 'lesson_quiz');  // TRẮC NGHIỆM (PMaker + quiz trong bài)
      const essays = assess.filter((a) => a.type === 'assignment');   // TỰ LUẬN

      // Điểm quy về thang 10
      const q10 = (q: any) => (Number(q.totalQuestions) > 0 ? (Number(q.correctAnswers) / Number(q.totalQuestions)) * 10 : Number(q.score) || 0);
      const a10 = (a: any) => (Number(a.maxScore) > 0 ? (Number(a.score) / Number(a.maxScore)) * 10 : Number(a.score) || 0);
      const mean = (arr: number[]) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);

      const quizScore10 = [...quizzes.map(q10), ...exams.map(a10)];
      setStatistics({
        completedLessons: stats?.completed_lessons || 0,
        totalLessons: stats?.total_lessons || 0,
        totalQuizzes: quizzes.length + exams.length,
        averageScore: mean(quizScore10),
        totalStudyTime: stats?.total_time_spent || 0,
      });

      setEssayStats({ count: essays.length, avg: mean(essays.map(a10)) });

      setLessonProgress((Array.isArray(progList) ? progList : [])
        .sort((a: any, b: any) => new Date(b.last_accessed_at || b.updated_at || 0).getTime() - new Date(a.last_accessed_at || a.updated_at || 0).getTime())
        .map((p: any) => ({
          id: p.id, lessonId: p.lesson_id, lessonTitle: p.lesson_title || p.lesson_id,
          progress: Math.min(100, Math.round(p.progress || 0)),
          timeSpent: p.time_spent || 0, status: p.status,
        })));

      const quizItems: QuizRow[] = quizzes.map((q: any) => ({
        id: q.id, quizTitle: q.quizTitle || q.quiz_title || 'Trắc nghiệm', attemptNumber: q.attemptNumber || 1,
        passed: q.passed, score: q.score,
        correctAnswers: q.correctAnswers ?? q.correct_answers ?? 0,
        wrongAnswers: q.wrongAnswers ?? q.wrong_answers ?? 0,
        _scoreText: '', _date: q.completedAt || q.createdAt || q.created_at,
      }));
      const examItems: QuizRow[] = exams.map((a: any) => ({
        // Cùng số liệu với bảng điểm PMaker của GV: lần làm, điểm/tổng, %, thời gian, cách nộp/mã đề, môn, rời màn hình
        id: a.id, quizTitle: (a.type === 'lesson_quiz' ? '🧩 ' : '📝 ') + a.title, attemptNumber: a.attempt_no || 1,
        passed: a.maxScore > 0 ? a.score >= 0.5 * a.maxScore : true,
        score: Math.round(a10(a) * 100) / 100, correctAnswers: 0, wrongAnswers: 0,
        _scoreText: a.type === 'lesson_quiz' ? `Đúng ${a.score}/${a.maxScore} câu`
          : `Điểm ${a.score}/${a.maxScore || 10}${a.percent != null ? ` · ${a.percent}%` : ''}${a.duration_seconds != null ? ` · ${fmtDur(a.duration_seconds)}` : ''}`,
        _meta: a.type === 'exam' ? [
          a.variant_code ? `Phiếu giấy · Mã đề ${a.variant_code}` : a.mode === 'paper' ? 'Phiếu giấy' : 'Làm online',
          a.subject, a.class_name ? `Lớp ${a.class_name}` : '',
          a.tab_switches ? `Rời màn hình ${a.tab_switches} lần` : '',
        ].filter(Boolean).join(' · ') : '',
        _date: a.submitted_at, _type: a.type,
      }));
      setQuizHistory([...quizItems, ...examItems]
        .sort((x, y) => new Date(y._date || 0).getTime() - new Date(x._date || 0).getTime()));

      setEssayHistory(essays.map((a: any) => ({
        id: a.id, title: a.title,
        passed: a.maxScore > 0 ? a.score >= 0.5 * a.maxScore : true,
        scoreText: `Điểm ${a.score}/${a.maxScore || 10}`, _date: a.submitted_at,
      })).sort((x: EssayRow, y: EssayRow) => new Date(y._date || 0).getTime() - new Date(x._date || 0).getTime()));

      setAchievements((achievs as any) || []);
    } catch (error) {
      console.error('❌ Failed to load study history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttemptDetail = async (attemptId: string) => {
    setLoadingAttemptId(attemptId);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      const response = await fetch(`${API_CONFIG.python.apiUrl}/study/my-assessments/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Không tải được bài làm chi tiết');
      setAttemptDetail(data);
    } catch (error: any) {
      alert(error.message || 'Không tải được bài làm chi tiết');
    } finally {
      setLoadingAttemptId(null);
    }
  };
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Vui lòng đăng nhập để xem lịch sử học tập</p>
          <a href="/dang-nhap" className="btn-primary px-6 py-3 rounded-lg inline-block">
            Đăng nhập
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải lịch sử học tập...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              📚 Lịch Sử Học Tập
            </h1>
            <p className="text-gray-600">
              Theo dõi tiến độ học tập và thành tích của bạn
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="grid grid-cols-5 sm:flex sm:gap-2 border-b">
            {[
              { key: 'overview', label: 'Tổng quan', icon: BarChart3 },
              { key: 'lessons', label: 'Bài học', icon: BookOpen },
              { key: 'quizzes', label: 'Trắc nghiệm', icon: Target },
              { key: 'essays', label: 'Tự luận', icon: FileText },
              { key: 'achievements', label: 'Thành tích', icon: Trophy },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 px-1 sm:px-6 py-2 sm:py-3 text-[12px] sm:text-[15px] leading-tight border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && statistics && (
            <OverviewTab statistics={statistics} essayStats={essayStats} achievementCount={achievements.length} />
          )}

          {activeTab === 'lessons' && (
            <LessonsTab lessons={lessonProgress} />
          )}

          {activeTab === 'quizzes' && (
            <QuizzesTab quizzes={quizHistory} onView={loadAttemptDetail} loadingAttemptId={loadingAttemptId} />
          )}

          {activeTab === 'essays' && (
            <EssaysTab essays={essayHistory} />
          )}

          {activeTab === 'achievements' && (
            <AchievementsTab achievements={achievements} />
          )}
        </div>
      </div>

      <Footer />
      {attemptDetail && <PmakerAttemptDetailModal detail={attemptDetail} onClose={() => setAttemptDetail(null)} />}
    </div>
  );
}

// Helper: giây → chuỗi đọc được
function formatStudyTime(seconds: number): { display: string; detail: string } {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let display = '';
  if (hours > 0) display = `${hours}h`;
  else if (minutes > 0) display = `${minutes}p`;
  else display = `${secs}s`;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}p`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return { display, detail: parts.join(' ') };
}

const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('vi-VN') : '—');
const fmtDur = (s: number) => `${Math.floor(s / 60)} phút ${String(s % 60).padStart(2, '0')} giây`;

// Overview Tab — 5 thẻ thống kê (thang điểm /10), khớp Tab Hồ sơ
function OverviewTab({ statistics, essayStats, achievementCount }: { statistics: Stats; essayStats: { count: number; avg: number }; achievementCount: number }) {
  const studyTime = formatStudyTime(statistics.totalStudyTime);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
      {/* Bài học hoàn thành */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <BookOpen className="w-8 h-8 text-blue-500" />
          <span className="text-3xl font-bold text-gray-800">{statistics.completedLessons}</span>
        </div>
        <h3 className="text-gray-600 text-sm">Bài học hoàn thành</h3>
        <p className="text-xs text-gray-500 mt-1">/ {statistics.totalLessons} bài</p>
      </div>

      {/* Bài trắc nghiệm */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Target className="w-8 h-8 text-green-500" />
          <span className="text-3xl font-bold text-gray-800">{statistics.totalQuizzes}</span>
        </div>
        <h3 className="text-gray-600 text-sm">Bài trắc nghiệm</h3>
        <p className="text-xs text-gray-500 mt-1">Điểm TB: {statistics.averageScore.toFixed(1)}/10</p>
      </div>

      {/* Bài tự luận */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <FileText className="w-8 h-8 text-teal-500" />
          <span className="text-3xl font-bold text-gray-800">{essayStats.count}</span>
        </div>
        <h3 className="text-gray-600 text-sm">Bài tự luận</h3>
        <p className="text-xs text-gray-500 mt-1">Điểm TB: {essayStats.avg.toFixed(1)}/10</p>
      </div>

      {/* Thời gian học */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Clock className="w-8 h-8 text-purple-500" />
          <span className="text-3xl font-bold text-gray-800">{studyTime.display}</span>
        </div>
        <h3 className="text-gray-600 text-sm">Thời gian học</h3>
        <p className="text-xs text-gray-500 mt-1">{studyTime.detail}</p>
      </div>

      {/* Huy hiệu */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Trophy className="w-8 h-8 text-yellow-500" />
          <span className="text-3xl font-bold text-gray-800">{achievementCount}</span>
        </div>
        <h3 className="text-gray-600 text-sm">Huy hiệu</h3>
        <p className="text-xs text-gray-500 mt-1">Thành tích đạt được</p>
      </div>
    </div>
  );
}

// Lessons Tab — bảng tiến độ từng bài
function LessonsTab({ lessons }: { lessons: LessonRow[] }) {
  if (lessons.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có bài học nào</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Bài học</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tiến độ</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Thời gian</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Trạng thái</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lessons.map(lesson => (
              <tr key={lesson.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-800">{lesson.lessonTitle}</div>
                  <div className="text-sm text-gray-500">{lesson.lessonId}</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${lesson.progress}%` }} />
                    </div>
                    <span className="text-sm font-semibold">{lesson.progress}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-600">
                  {formatStudyTime(lesson.timeSpent).detail}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    lesson.status === 'completed' ? 'bg-green-100 text-green-800' :
                    lesson.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {lesson.status === 'completed' ? 'Hoàn thành' :
                     lesson.status === 'in_progress' ? 'Đang học' : 'Chưa bắt đầu'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Quizzes Tab — trắc nghiệm trong bài + đề PMaker (điểm /10)
function QuizzesTab({ quizzes, onView, loadingAttemptId }: {
  quizzes: QuizRow[];
  onView: (attemptId: string) => void;
  loadingAttemptId: string | null;
}) {
  if (quizzes.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có bài trắc nghiệm nào</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {quizzes.map(quiz => (
        <div key={quiz.id} className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-800">{quiz.quizTitle}</h3>
              <p className="text-sm text-gray-500">Lần {quiz.attemptNumber}</p>
              {quiz._meta && <p className="text-sm text-gray-600 mt-0.5">{quiz._meta}</p>}
            </div>
            <div className={`text-3xl font-bold ml-3 ${quiz.passed ? 'text-green-600' : 'text-red-600'}`}>
              {quiz.score}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600 pt-3 border-t">
            {quiz._scoreText
              ? <span>{quiz._scoreText}</span>
              : <span>✓ {quiz.correctAnswers} đúng · ✗ {quiz.wrongAnswers} sai</span>}
            <span className={quiz.passed ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
              {quiz.passed ? 'Đạt' : 'Chưa đạt'}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-2">{quiz._date ? new Date(quiz._date).toLocaleString('vi-VN') : '—'}</div>
          {quiz._type === 'exam' && (
            <button
              onClick={() => onView(quiz.id)}
              disabled={loadingAttemptId === quiz.id}
              className="mt-3 w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
            >
              {loadingAttemptId === quiz.id ? 'Đang tải...' : 'Xem chi tiết từng câu'}
            </button>
          )}        </div>
      ))}
    </div>
  );
}

// Essays Tab — bài tập tự luận đã chấm
function EssaysTab({ essays }: { essays: EssayRow[] }) {
  if (essays.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có bài tự luận nào</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {essays.map(es => (
        <div key={es.id} className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-800 truncate">✍️ {es.title}</h3>
            <p className="text-sm text-gray-500">{es.scoreText}</p>
            <div className="text-xs text-gray-400 mt-1">{fmtDate(es._date)}</div>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full shrink-0 ${es.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {es.passed ? 'Đạt' : 'Chưa đạt'}
          </span>
        </div>
      ))}
    </div>
  );
}

// Achievements Tab
function AchievementsTab({ achievements }: { achievements: Achievement[] }) {
  if (achievements.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có thành tích nào</p>
        <p className="text-sm text-gray-500 mt-2">
          Tiếp tục học tập để mở khóa huy hiệu!
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {achievements.map(achievement => (
        <div key={achievement.id} className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl shadow-lg p-6 border-2 border-yellow-200">
          <div className="text-5xl mb-4 text-center">{achievement.icon}</div>
          <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
            {achievement.title}
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            {achievement.description}
          </p>
          <div className="text-xs text-gray-500 text-center">
            {new Date(achievement.earnedAt).toLocaleDateString('vi-VN')}
          </div>
        </div>
      ))}
    </div>
  );
}
